/**
 * CallDisplay - Shows call information and status
 */

import React from 'react';
import { CallState } from '../../hooks/useSIP';

function CallDisplay({
  callState,
  callInfo,
  callDuration,
  callQuality,
  isMuted,
  isLocalHold,
  isRemoteHold,
  transferState,
  dialNumber,
  onDialNumberChange,
  addCallMode,
  lastDialed,
  onRedial,
  onUseLastDialed
}) {
  // Format duration as MM:SS
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Get quality color
  const getQualityColor = (mos) => {
    const mosNum = parseFloat(mos);
    if (mosNum >= 4.0) return 'var(--cgs-green)';
    if (mosNum >= 3.0) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };
  
  // Get call state label
  const getStateLabel = () => {
    if (transferState) {
      return transferState.step === 'consulting' 
        ? 'Consulting...' 
        : 'Ready to Transfer';
    }
    if (isLocalHold) return 'On Hold';
    if (isRemoteHold) return 'Held by Remote';
    
    switch (callState) {
      case CallState.INCOMING: return 'Incoming Call';
      case CallState.RINGING: return 'Ringing...';
      case CallState.CONNECTING: return 'Connecting...';
      case CallState.CONNECTED: return 'Connected';
      case CallState.CONFERENCING: return 'Conference';
      case CallState.TRANSFERRING: return 'Transferring...';
      default: return '';
    }
  };
  
  // Idle state - show dial input + redial of last number
  if (callState === CallState.IDLE) {
    return (
      <div className="cx-softphone__display">
        <input
          type="tel"
          className="cx-softphone__dial-input"
          value={dialNumber}
          onChange={(e) => onDialNumberChange(e.target.value)}
          placeholder="Enter number..."
        />
        {lastDialed && (
          <div className="cx-softphone__redial-row">
            <button
              type="button"
              className="cx-softphone__redial-btn"
              onClick={onRedial}
              title={`Redial ${lastDialed}`}
            >
              ↻ Redial {lastDialed}
            </button>
            <button
              type="button"
              className="cx-softphone__redial-edit-btn"
              onClick={onUseLastDialed}
              title="Copy last number into the dial box"
            >
              ✏️
            </button>
          </div>
        )}
      </div>
    );
  }
  
  // Active call state
  return (
    <div className="cx-softphone__display cx-softphone__display--active">
      {/* 2026-07-26: Add Call mode — show a number input so the agent can
          see/type the 3rd-party number while the caller stays on the line.
          Pressing Call (in CallControls) routes it through startConference. */}
      {addCallMode && (
        <div className="cx-softphone__addcall-entry" style={{ marginBottom: '8px' }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '4px' }}>
            Add a party — enter number, then press Call
          </div>
          <input
            type="tel"
            className="cx-softphone__dial-input"
            value={dialNumber}
            onChange={(e) => onDialNumberChange(e.target.value)}
            placeholder="Enter 3rd-party number..."
            autoFocus
          />
        </div>
      )}
      {/* Status badges */}
      <div className="cx-softphone__status-badges">
        {isMuted && (
          <span className="cx-softphone__badge cx-softphone__badge--muted">
            🔇 Muted
          </span>
        )}
        {isLocalHold && (
          <span className="cx-softphone__badge cx-softphone__badge--hold">
            ⏸️ Hold
          </span>
        )}
        {isRemoteHold && (
          <span className="cx-softphone__badge cx-softphone__badge--held">
            ⏸️ Held
          </span>
        )}
      </div>
      
      {/* Call state */}
      <div className="cx-softphone__call-state">
        {getStateLabel()}
      </div>
      
      {/* Remote party info */}
      <div className="cx-softphone__remote-party">
        <span className="cx-softphone__remote-number">
          {callInfo?.remoteNumber || 'Unknown'}
        </span>
        {callInfo?.remoteName && callInfo.remoteName !== callInfo.remoteNumber && (
          <span className="cx-softphone__remote-name">
            {callInfo.remoteName}
          </span>
        )}
      </div>
      
      {/* Duration */}
      {callState === CallState.CONNECTED && (
        <div className="cx-softphone__duration">
          {formatDuration(callDuration)}
        </div>
      )}
      
      {/* Call quality */}
      {callQuality && callState === CallState.CONNECTED && (
        <div className="cx-softphone__quality">
          <span 
            className="cx-softphone__quality-bar"
            style={{ 
              '--quality-color': getQualityColor(callQuality.mos),
              '--quality-level': `${(parseFloat(callQuality.mos) / 4.5) * 100}%`
            }}
          />
          <span className="cx-softphone__quality-label">
            MOS: {callQuality.mos}
          </span>
        </div>
      )}
      
      {/* Incoming call animation */}
      {callState === CallState.INCOMING && (
        <div className="cx-softphone__incoming-animation">
          <span className="cx-softphone__ring-icon">📞</span>
        </div>
      )}
    </div>
  );
}

export default CallDisplay;
